import { Typography, Paper, Chip, Box, IconButton, CircularProgress } from '@mui/material';
import Page from '../../../../components/page/Page';
import useThemeMediaQuery from '../../../../../core/hooks/useThemeMediaQuery';
import { useCallback, useEffect, useState } from 'react';
import Header from '../../../../components/administration/containers/Header';
import { useGlobalCore } from '../../../../../core/modules/global';
import { UPDATE_CONTAINER_DIALOG } from '../../../../../core/constants/dialog';
import CreateDialog from '../../../../components/administration/containers/CreateDialog';
import UpdateDialog from '../../../../components/administration/containers/UpdateDialog';
import PrintIcon from '@mui/icons-material/Print';
import EditIcon from '@mui/icons-material/Edit';
import { useMeCore } from '../../../../../core/modules/me';
import { useNavigationCore } from '../../../../../core/router/navigation';
import { useCompanyCore } from '../../../../../core/modules/company';
import { useContainerCore } from '../../../../../core/modules/container';
import { Container } from '../../../../../core/interfaces/container.interface';
import { useMasterCore } from '../../../../../core/modules/master';
import { useBusinessCore } from '../../../../../core/modules/business';
import { useLocationCore } from '../../../../../core/modules/location';
import { useWarehouseCore } from '../../../../../core/modules/warehouse';
import { DESTINY_CONTAINER_TYPE_COLOR, DESTINY_CONTAINER_TYPE_LABEL } from '../../../../../core/constants/containerTypes';
import { useLocation } from 'react-router-dom';
import { usePrintLabel } from '../../../../../core/hooks/usePrintLabel';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

const Containers = () => {
  const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down('lg'));
  const { user } = useMeCore();
  const { companyId } = useCompanyCore();
  const location = useLocation();
  const { containers, options, getContainers, setContainer, resetOptions, setOptions } = useContainerCore();
  const { getCategories, getTypes, getPriceRanges, containerTypes } = useMasterCore();
  const { getBusinesses } = useBusinessCore();
  const { openDialog } = useGlobalCore();
  const { goToWelcome } = useNavigationCore();
  const { locations } = useLocationCore();
  const { warehouses, getWarehouses } = useWarehouseCore();
  const { printDestinationContainer } = usePrintLabel();

  const [initialSearchValue, setInitialSearchValue] = useState<string>('');
  const [initialSearchField, setInitialSearchField] = useState<'code' | 'name'>('code');
  const [gridReady, setGridReady] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    if (
      !user ||
      (
        !user.privileges.superAdmin &&
        !user.privileges.readContainer &&
        !user.privileges.writeContainer
      )
    ) {
      goToWelcome();
    } else {
      resetOptions();
    }
  }, [user, goToWelcome, resetOptions]);

  // Efecto separado para manejar parámetros de búsqueda automática
  useEffect(() => {
    if (companyId && location.search) {
      // Verificar si hay parámetros de búsqueda en la URL
      const searchParams = new URLSearchParams(location.search);
      const searchTerm = searchParams.get('search');
      const searchField = searchParams.get('field') as 'code' | 'name' | undefined;
      
      if (searchTerm && searchField) {
        console.log('Aplicando búsqueda automática de contenedor:', { searchTerm, searchField });
        
        // Guardar los valores para pasarlos al Header
        setInitialSearchValue(searchTerm);
        setInitialSearchField(searchField);
        
        // Aplicar búsqueda automática
        setOptions({ [searchField]: searchTerm });
        
        // Limpiar la URL después de aplicar la búsqueda
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('search');
        newUrl.searchParams.delete('field');
        window.history.replaceState({}, '', newUrl.toString());
      }
    }
  }, [companyId, location.search, setOptions]);

  useEffect(() => {
    // Render diferido para evitar ResizeObserver loop al cambiar de vista
    const t = setTimeout(() => setGridReady(true), 0);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (companyId) {
      // Resetear el estado de carga cuando cambien los filtros
      setHasLoaded(false);
      getContainers(companyId, options, () => {
        // onSuccess callback - cuando se cargan los contenedores
        setHasLoaded(true);
      }, () => {
        // onFailure callback - también marcamos como cargado para mostrar el mensaje de error o vacío
        setHasLoaded(true);
      });
      getTypes(companyId, 'container');
      getWarehouses(companyId);
    }
  }, [getContainers, companyId, options, getTypes, getWarehouses]);
  
  const selectContainer = useCallback(
    (container: Container) => {
      if (companyId) {
        getBusinesses(companyId);
        getTypes(companyId, 'container');
        getCategories(companyId, 'container');
        getTypes(companyId, 'channel');
        getPriceRanges(companyId);
        setContainer(container);
      }
      openDialog(UPDATE_CONTAINER_DIALOG);
    },
    [companyId, getBusinesses, getCategories, getPriceRanges, getTypes, openDialog, setContainer]
  );

  const print = useCallback((ev: any, container: Container) => {
    ev.stopPropagation();
    printDestinationContainer(container);
  }, [printDestinationContainer]);

  // Función utilitaria para validar y formatear fechas
  const formatDate = (dateString: string | number | null | undefined): string => {
    if (!dateString) return 'N/A';
    
    // El backend envía Unix timestamp en segundos, JavaScript necesita milisegundos
    const timestamp = typeof dateString === 'number' ? dateString * 1000 : dateString;
    const date = new Date(timestamp);
    
    // Verificar si la fecha es válida
    if (isNaN(date.getTime())) {
      return 'N/A';
    }
    
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Definir columnas del DataGrid
  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'Folio',
      flex: 0.8,
      sortable: true,
      cellClassName: 'flex items-center p-8',
    },
    {
      field: 'code',
      headerName: 'Código',
      flex: 1,
      sortable: true,
      cellClassName: 'flex items-center p-8',
    },
    {
      field: 'description',
      headerName: 'Descripción',
      flex: 1.5,
      sortable: true,
      cellClassName: 'flex items-center p-8',
    },
    {
      field: 'container_type_id',
      headerName: 'Tipo de contenedor',
      flex: 1.2,
      sortable: true,
      cellClassName: 'flex items-center p-8',
      renderCell: (params: GridRenderCellParams) => {
        const row: any = params.row;
        const containerType = (containerTypes || []).find((t) => t.id === row.container_category.container_type_id);
        return containerType ?  `(${containerType.description || ''}) ${containerType.name}` : '-';
      },
    },
    {
      field: 'container_status_id',
      headerName: 'Estado',
      flex: 1,
      sortable: true,
      cellClassName: 'flex items-center p-8',
      renderCell: (params: GridRenderCellParams) => {
        const row: any = params.row;
        return (
          <Chip 
            label={DESTINY_CONTAINER_TYPE_LABEL[row.container_status_id] || 'Desconocido'} 
            size="small" 
            color={DESTINY_CONTAINER_TYPE_COLOR[row.container_status_id] || 'default'} 
          />
        );
      },
    },
    {
      field: 'location_code',
      headerName: 'Código Ubicación',
      flex: 1,
      sortable: true,
      cellClassName: 'flex items-center p-8',
      renderCell: (params: GridRenderCellParams) => {
        const row: any = params.row;
        const location = locations.find(loc => loc.id === row.location_id);
        return location ? location.code : '-';
      },
    },
    {
      field: 'warehouse_code',
      headerName: 'Código Bodega',
      flex: 1,
      sortable: true,
      cellClassName: 'flex items-center p-8',
      renderCell: (params: GridRenderCellParams) => {
        const row: any = params.row;
        const location = locations.find(loc => loc.id === row.location_id);
        if (location) {
          const warehouse = warehouses.find(w => w.id === location.warehouse_id);
          return warehouse ? warehouse.code : '-';
        }
        return '-';
      },
    },
    {
      field: 'created_at',
      headerName: 'Fecha de creación',
      flex: 1.2,
      sortable: true,
      cellClassName: 'flex items-center p-8',
      renderCell: (params: GridRenderCellParams) => {
        const row: any = params.row;
        return formatDate(row.created_at);
      },
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      flex: 1,
      sortable: false,
      cellClassName: 'flex items-center justify-end p-8',
      renderCell: (params: GridRenderCellParams) => {
        const container = params.row as Container;
        return (
          <div className="flex gap-2">
            <IconButton 
              aria-label="imprimir" 
              size="small"
              onClick={(ev) => print(ev, container)}
            >
              <PrintIcon fontSize="small" />
            </IconButton>
            <IconButton 
              aria-label="editar" 
              size="small"
              onClick={() => selectContainer(container)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <Page
        header={
          <Header 
            initialSearch={initialSearchValue || options.code || ''}
            initialField={initialSearchField}
          />
        }
        content={
          <div className="w-full my-16 px-24 md:px-32">
            <Paper className="flex flex-col flex-auto items-center shadow rounded-2xl overflow-hidden">
              <div className="w-full p-32">
                {!hasLoaded ? (
                  <div className="flex flex-1 items-center justify-center h-full" style={{ minHeight: '400px' }}>
                    <CircularProgress />
                  </div>
                ) : !containers.length ? (
                  <div className="flex flex-1 items-center justify-center h-full" style={{ minHeight: '400px' }}>
                    <Typography color="text.secondary" variant="h5">
                      No hay contenedores creados
                    </Typography>
                  </div>
                ) : gridReady ? (
                  <Box sx={{ height: '70vh', overflow: 'auto' }}>
                    <DataGrid
                      rows={containers}
                      columns={columns}
                      sx={{ border: 0 }}
                      initialState={{ sorting: { sortModel: [{ field: 'code', sort: 'asc' }] } }}
                      disableColumnFilter
                      disableRowSelectionOnClick
                      disableMultipleRowSelection
                      hideFooter
                      getRowHeight={() => 'auto'}
                      pagination={undefined}
                    />
                  </Box>
                ) : null}
              </div>
            </Paper>
          </div>
        }
        scroll={isMobile ? 'normal' : 'content'}
      />
      <CreateDialog />
      <UpdateDialog />
    </>
  );
};

export default Containers;
